import { Box, Flex, useDisclosure } from "@chakra-ui/core";
import { css } from "@emotion/core";
import exitIntent from "exit-intent";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { AuthWhatsOpen } from "../home/HomeConstants";
import { AuthModal, NavbarLoggedIn, NavbarLogin } from "./NavAuthComponents";
import { NavbarCart, NavbarContent, PopupModal } from "./NavComponents";
import {
  authConnector,
  openAuthModal,
  ReduxNavProps,
  useCompetitionConsentStore,
  useOpenPopupStore,
  usePopupStore,
} from "./NavConstants";
import CompetitionConsentModal from "../popups/CompetitionConsentModal";
import {
  getCompetitionConsent,
  shouldAskConsent,
  submitCompetitionConsent,
} from "../../services/competition-consent.service";

function Navbar(reduxProps: ReduxNavProps): JSX.Element {
  const authUseDisclosure = useDisclosure();
  const location = useLocation();
  const [authWhatsOpen, setAuthWhatsOpen] = useState(AuthWhatsOpen.login);

  const [, setPopupWhatsOpen] = usePopupStore();
  const [, setPopupOpen] = useOpenPopupStore();
  const [competitionConsent, setCompetitionConsent] =
    useCompetitionConsentStore();
  const [isConsentModalOpen, setConsentModalOpen] = useState(false);
  const [isConsentChange, setConsentChange] = useState(false);

  useEffect(() => {
    if (reduxProps.isLoggedIn && !reduxProps.userInfo) {
      reduxProps.AuthActions.getUserInfo();
    }
    return () => {};
  }, [
    reduxProps.isLoggedIn,
    reduxProps.cartItems,
    reduxProps.AuthActions,
    reduxProps.userInfo,
  ]);

  // „Pizza Sprint 30” nyereményjáték: a hozzájárulás állapotát bejelentkezés
  // után egyszer kérdezzük le, és ha a kampány fut és még nincs válasz, azonnal
  // felugrik a kötelező ablak. Kijelentkezéskor ürítjük, hogy a következő
  // felhasználónál újra lefusson.
  useEffect(() => {
    if (!reduxProps.isLoggedIn) {
      setCompetitionConsent(null);
      setConsentModalOpen(false);
      return;
    }
    if (competitionConsent !== null) return;
    let isActive = true;
    getCompetitionConsent().then((status) => {
      if (!isActive || !status) return;
      setCompetitionConsent(status);
      if (shouldAskConsent(status)) {
        setConsentChange(false);
        setConsentModalOpen(true);
      }
    });
    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxProps.isLoggedIn]);

  async function decideCompetitionConsent(accepted: boolean) {
    const saved = await submitCompetitionConsent(accepted, isConsentChange);
    if (saved) {
      const status = await getCompetitionConsent();
      if (status) setCompetitionConsent(status);
    }
    // Sikertelen mentésnél is bezárjuk: a felhasználót nem tartjuk fogva egy
    // szerverhiba miatt — a kérdés a következő betöltéskor újra feljön.
    setConsentModalOpen(false);
  }

  const removeExitIntent = exitIntent({
    threshold: 12,
    maxDisplays: 1,
    eventThrottle: 200,
    onExitIntent: () => {
      setPopupWhatsOpen("exit");
      setPopupOpen(true);
    },
  });

  // ///////////////////////
  // This disables the popup
  // ///////////////////////
  removeExitIntent();

  useEffect(() => {
    return () => {
      removeExitIntent();
    };
  }, [removeExitIntent]);

  useEffect(() => {
    if (location.pathname.split("/")[2] === "cart" && !reduxProps.isLoggedIn) {
      if (setAuthWhatsOpen) setAuthWhatsOpen(AuthWhatsOpen.login);
      openAuthModal(authUseDisclosure);
    }
  }, [location.pathname, reduxProps.isLoggedIn]);

  return (
    <>
      <AuthModal
        reduxProps={reduxProps}
        whatsOpen={authWhatsOpen}
        setwhatsOpen={setAuthWhatsOpen}
        disclosure={authUseDisclosure}
      />
      <PopupModal reduxProps={reduxProps} />
      {isConsentModalOpen && !!competitionConsent?.competition && (
        <CompetitionConsentModal
          isOpen
          competition={competitionConsent.competition}
          isDismissible={isConsentChange}
          isChange={isConsentChange}
          initialAccepted={competitionConsent.accepted}
          onDecide={decideCompetitionConsent}
          onClose={() => setConsentModalOpen(false)}
        />
      )}
      <Box
        as="nav"
        position="relative"
        maxW={["100%", "540px", "720px", "960px", "1140px"]}
        w="100%"
        flexShrink={0}
        zIndex={300}
        pb={4}
        css={css`
          * {
            z-index: 300;
          }
        `}
      >
        <Flex
          w="100%"
          justifyContent="space-between"
          alignItems="center"
          display={["none", "none", "none", "flex"]}
          py={2}
        >
          <NavbarLogin
            display={reduxProps.isLoggedIn ? "none" : "initial"}
            disclosure={authUseDisclosure}
            setwhatsOpen={setAuthWhatsOpen}
          />
          <NavbarLoggedIn
            display={reduxProps.isLoggedIn ? "initial" : "none"}
            disclosure={authUseDisclosure}
            setwhatsOpen={setAuthWhatsOpen}
            onOpenCompetition={() => {
              setConsentChange(true);
              setConsentModalOpen(true);
            }}
          />
          <NavbarCart reduxProps={reduxProps} />
        </Flex>
        <NavbarContent
          setwhatsOpen={setAuthWhatsOpen}
          reduxProps={reduxProps}
          disclosure={authUseDisclosure}
        />
      </Box>
    </>
  );
}

export default authConnector(Navbar);
